import { Box, CircularProgress, Collapse, FormHelperText, Stack, svgIconClasses, Typography } from "@mui/material";
import {
    Button,
    IconButton,
    ControlledCheckbox,
    ControlledTextField,
    Form,
    Link,
    PasswordStrengthIndicator,
    useHookForm,
    palette,
} from "@powerdiary/components-library/lib";
import type { ZXCVBNResult } from "zxcvbn";
import { Eye, EyeOff } from "lucide-react";

import { BaseSyntheticEvent, MouseEvent, useCallback, useState } from "react";
import { Controller } from "react-hook-form";
import { useClientPortalConfigContext } from "../../../../context";
import { useClientPortalClientRegister } from "./useClientPortalUserRegister";
import { generateDefaultValues, registerSchema, RegisterType, Step } from "./RegisterDialogForm.helpers";
import { LoginDialogMode, RegisterMode } from "../../Account.types";
import { VerificationCodeInput } from "../../../common/VerificationCodeInput";
import RecaptchaNotice from "../../../Shared/RecaptchaNotice";

interface IRegisterFormProps {
    registerCompleted?: () => void;
    registerMode: RegisterMode;
    setMode?: (mode: LoginDialogMode) => void;
}

export default function RegisterDialogForm({ registerCompleted, registerMode, setMode }: IRegisterFormProps) {
    const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { clientPortalConfig } = useClientPortalConfigContext();

    const isSmsType = clientPortalConfig.tokenVia === "sms";

    const form = useHookForm({
        defaultValues: generateDefaultValues(
            clientPortalConfig.termsAndConditions,
            clientPortalConfig.countryCode,
            isSmsType
        ),
        schema: registerSchema,
        mode: "onChange",
    });
    const { formState, getValues, watch, setValue, trigger, control } = form;
    const { register, isPending, resendVerificationCode, isResending } = useClientPortalClientRegister(
        form,
        registerMode
    );

    const password = watch("password") || "";

    const handleRegister = async (values: RegisterType, event: BaseSyntheticEvent) => {
        await register(values, event, registerCompleted);
    };

    const handleResult = useCallback(
        (result?: ZXCVBNResult) => {
            if (!result) return;

            const prevScore = getValues("passwordScore");

            setValue("passwordScore", result?.score || 0);
            if (prevScore !== result?.score) {
                trigger("password").catch(() => {});
            }
        },
        [getValues, setValue, trigger]
    );

    const verificationStep = getValues("step");
    const strongPassMsg = "strong password required";

    return (
        <Form form={form} onSubmit={handleRegister} autoComplete="off" data-test-id="dialog_register_form">
            <Stack gap="16px">
                {isPending ? (
                    <CircularProgress sx={{ alignSelf: "center" }} />
                ) : (
                    <>
                        {verificationStep === Step.Initial && (
                            <>
                                <ControlledTextField dataTestIdSuffix="firstName" label="First Name" name="firstName" />
                                <ControlledTextField dataTestIdSuffix="lastName" label="Last Name" name="lastName" />
                                <ControlledTextField dataTestIdSuffix="email" type="email" label="Email" name="email" />
                                <ControlledTextField
                                    dataTestIdSuffix="password"
                                    type={showPassword ? "text" : "password"}
                                    label="Password"
                                    name="password"
                                    InputProps={{
                                        autoComplete: "new-password",
                                        endAdornment: (
                                            <IconButton
                                                variant="translucent"
                                                size="extraSmall"
                                                iconColor={palette.neutral["900"]}
                                                icon={showPassword ? EyeOff : Eye}
                                                onClickHandler={() => setShowPassword(!showPassword)}
                                                data-test-id="button_toggle_password_visibility"
                                                title={showPassword ? "Hide password" : "Show password"}
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                                boxStyles={{ marginRight: "-8px" }}
                                            />
                                        ),
                                    }}
                                />
                                <PasswordStrengthIndicator
                                    password={password}
                                    handlePasswordValidation={handleResult}
                                    sx={{
                                        marginTop: "-12px",
                                    }}
                                    customLabels={{
                                        0: `Very weak - ${strongPassMsg}`,
                                        1: `Weak - ${strongPassMsg}`,
                                    }}
                                />
                                <ControlledTextField
                                    dataTestIdSuffix="mobileNumber"
                                    label="Mobile Number"
                                    name="phoneNumber"
                                />
                            </>
                        )}

                        {verificationStep === Step.Verification && (
                            <Stack gap={"12px"}>
                                <Typography variant="paragraphLargeBold">Enter Verification Code</Typography>
                                <Typography variant="paragraphSmallRegular">
                                    Please enter the activation code received.
                                </Typography>
                                <Controller
                                    name="verificationCode"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <VerificationCodeInput
                                            value={field.value || ""}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            dataTestIdPrefix="verificationCode"
                                        />
                                    )}
                                />
                                <Typography variant="paragraphSmallRegular">
                                    {`Didn't receive the verification code?`}
                                </Typography>
                                <Link
                                    variant="paragraphSmallRegular"
                                    data-test-id="resend_verification_code"
                                    onClick={() => void resendVerificationCode()}
                                    sx={{
                                        cursor: isResending ? "wait" : "pointer",
                                        pointerEvents: isResending ? "none" : "auto",
                                        opacity: isResending ? 0.6 : 1,
                                        marginTop: "-4px",
                                    }}
                                >
                                    {isResending ? "Resending..." : "Resend the verification code"}
                                </Link>
                                {clientPortalConfig.termsAndConditions && (
                                    <>
                                        <Stack>
                                            <Box>
                                                <ControlledCheckbox
                                                    label=""
                                                    name="conditions"
                                                    dataTestIdSuffix="conditions"
                                                    sx={{ [`& .${svgIconClasses.root}`]: { fontSize: "18px" } }}
                                                />
                                                <Typography
                                                    component="span"
                                                    variant="paragraphSmallRegular"
                                                    sx={{ marginLeft: "-14px" }}
                                                >
                                                    I accept the{" "}
                                                    <Link
                                                        variant="paragraphSmallRegular"
                                                        href="#"
                                                        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                                                            event.preventDefault();
                                                            setShowTermsAndConditions(!showTermsAndConditions);
                                                        }}
                                                        data-test-id="link_terms_and_conditions"
                                                    >
                                                        terms and conditions of use
                                                    </Link>
                                                </Typography>
                                            </Box>
                                            {formState.isSubmitted && formState.errors.conditions && (
                                                <FormHelperText error data-test-id="text_conditions_error">
                                                    {formState.errors.conditions.message}
                                                </FormHelperText>
                                            )}
                                        </Stack>
                                        <Collapse in={showTermsAndConditions}>
                                            <Typography
                                                data-test-id="text_terms_and_conditions"
                                                variant="body2"
                                                id="terms-and-Conditions"
                                            >
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: clientPortalConfig.termsAndConditions,
                                                    }}
                                                />
                                            </Typography>
                                        </Collapse>
                                    </>
                                )}
                            </Stack>
                        )}

                        {formState?.errors?.root && (
                            <FormHelperText error data-test-id="form_error_root_message">
                                <>{formState.errors.root.message}</>
                            </FormHelperText>
                        )}
                    </>
                )}

                <Button fullWidth type="submit" variant="contained" disabled={isPending} data-test-id="button_register">
                    {verificationStep === Step.Initial ? "Get verification code" : "Register"}
                </Button>

                <RecaptchaNotice />

                {verificationStep === Step.Initial && (
                    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "16px" }}>
                        <Typography
                            variant="paragraphSmallRegular"
                            textAlign="center"
                            data-test-id="text_verification_code_info"
                        >
                            {`A verification code will be sent to your ${isSmsType ? "mobile number" : "email"}.`}
                        </Typography>
                        {setMode && (
                            <Typography variant="paragraphSmallRegular" textAlign="center" data-test-id="link_login">
                                Already registered?{" "}
                                <Link onClick={() => setMode(LoginDialogMode.Login)}>Log in here</Link>
                            </Typography>
                        )}
                    </Box>
                )}
            </Stack>
        </Form>
    );
}
